import C from "../Constants";

import EventHelperCommon from "../scheduler-event-helper/common";

export default async (project, changes) => {
  const eventStore = project.eventStore;
  // 追加、変更されたイベント差分を取得
  const { added, modified } = changes;

  if (added.length > 0) {
    for (const event of added) {
      // 計画中はスキップ
      if (event.scheduling_status === C.SCHEDULING_STATUS.PLANNED) {
        continue;
      }

      // 下船中様態が追加された場合
      if (event.event_type === C.SCHEDULING_EVENT_TYPE.ASHORE) {
        await _processAddAshoreEventAsync(event, eventStore);
      }
    }
  }

  // 下船中様態編集された場合
  if (modified.length > 0) {
    for (const event of modified) {
      // 計画中はスキップ
      if (event.scheduling_status === C.SCHEDULING_STATUS.PLANNED) {
        continue;
      }

      // 下船中様態が変更された場合
      if (event.event_type === C.SCHEDULING_EVENT_TYPE.ASHORE) {
        await _processAddAshoreEventAsync(event, eventStore);
      }
    }
  }
};

const _processAddAshoreEventAsync = async (addedEvent, eventStore) => {
  const targetEvents = _getTargetEvents(eventStore, addedEvent, false);

  // 重なっているイベントを処理
  // 削除、分割処理を行う
  await _processOverlappingEventsAsync(addedEvent, targetEvents, eventStore);

  // イベントサイズ調整
  await _ajastPushAsync(addedEvent, targetEvents);

  // 乗船中イベントの乗船期間ID振り直し
  await _reAssignManningVesselCrewId(eventStore, addedEvent.resourceId);

  // イベント結合
  await _concateEventsAsync(eventStore, addedEvent.resourceId);

  // イベントのコピーと削除を適用
  await EventHelperCommon.applyReplaceAndDeleteAsync(eventStore);
};

/**
 * 包括しているイベントを処理する。
 * 削除、分割処理を行う。
 * @param {EventModel} editedEvent
 * @param {Array<EventModel>} targetEvents
 * @param {EventStore} eventStore
 */
const _processOverlappingEventsAsync = async (
  editedEvent,
  targetEvents,
  eventStore
) => {
  // 重なっている、または隣接しているイベントを検索
  const overlappingOrAdjacentEvents = targetEvents.filter(event => {
    return EventHelperCommon.isOverlappingOrAdjacent(editedEvent, event);
  });

  // 追加イベントを包括しているイベントを検索
  const addedContains = overlappingOrAdjacentEvents.filter(event => {
    return EventHelperCommon.isContained(editedEvent, event);
  });
  // 追加イベントを含むイベントが存在する場合、下船中様態のイベントを削除
  for (const containtsEvent of addedContains) {
    // 同じ種類のイベントの場合は追加されたイベントを削除
    if (_isSameEventType(editedEvent, containtsEvent)) {
      EventHelperCommon.softDelete(editedEvent);
      // 異なる場合は分割処理
    } else {
      // 追加イベントが内部包括している場合は分割処理
      if (
        containtsEvent.startDate.getTime() < editedEvent.startDate.getTime() &&
        containtsEvent.endDate.getTime() > editedEvent.endDate.getTime()
      ) {
        await _splitEventAsync(
          containtsEvent,
          eventStore,
          editedEvent.startDate,
          editedEvent.endDate
        );
      }
    }
  }

  // 追加イベントが包括しているイベントは削除
  const contains = overlappingOrAdjacentEvents.filter(event => {
    return EventHelperCommon.isContained(event, editedEvent);
  });
  for (const targetEvent of contains) {
    EventHelperCommon.softDelete(targetEvent);
  }
};

/**
 * 乗船期間IDを振り直す
 * @param {Store} store
 * @param {String} resourceId
 */
const _reAssignManningVesselCrewId = async (store, resourceId) => {
  const targetEvents = store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.resourceId === resourceId &&
      event.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL &&
      !EventHelperCommon.isSoftDeleted(event)
    );
  }, true);
  targetEvents.sort(EventHelperCommon.sortByStartDate);
  // 重複のない船舶IDを取得
  const uniqueVesselIds = new Set(
    targetEvents.map(event => event.enterprise_manning_vessel_id)
  );

  for (const vesselId of uniqueVesselIds) {
    // 同じ船舶IDのイベントを取得
    const vesselEvents = targetEvents.filter(
      event => event.enterprise_manning_vessel_id === vesselId
    );

    if (vesselEvents.length > 1) {
      const previousEvent = vesselEvents[0];
      _processReAssigin(vesselEvents.slice(1), previousEvent);
    }
  }
};

/**
 * 乗船中イベントの乗船期間IDを更新するヘルパー関数
 * @param {EventModel} targetEvents
 * @param {EventModel} previousEvent
 * @returns
 */
const _processReAssigin = (targetEvents, previousEvent) => {
  if (targetEvents.length === 0) {
    return;
  }

  // 判定対象のイベントを取得
  const currentEvent = targetEvents[0];

  // 事前に乗船期間ID保持
  const currentManningVesselCrewId =
    currentEvent.enterprise_manning_vessel_crew_id;

  // 乗船期間IDの更新が必要かを判定
  // 更新理由によって処理を分岐
  const reason = _shouldUpdateVesselCrewId(previousEvent, currentEvent);

  if (reason === UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE) {
    // 更新不要の場合は次のイベントを処理
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // 判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.PREVIOUS_STATUS_WAS_PLANNED) {
    // 前の乗船期間IDを判定対象の乗船期間IDに更新
    EventHelperCommon.changeEnterpriseManningVesselCrewId(
      previousEvent,
      currentEvent.enterprise_manning_vessel_crew_id
    );
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.CURRENT_STATUS_WAS_PLANNED) {
    // 判定対象の乗船期間IDを前の乗船期間IDに更新
    EventHelperCommon.changeEnterpriseManningVesselCrewId(
      currentEvent,
      previousEvent.enterprise_manning_vessel_crew_id
    );
    _processReAssigin(targetEvents.slice(1), currentEvent);
  }

  // イベントが繋がっているが、乗船期間IDが異なる
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_CREW_ID) {
    // 判定対象の乗船期間IDと同じイベントを前のイベントの乗船期間IDに更新
    // 乗船期間IDが変わった時に再起呼び出し
    for (let i = 0; i < targetEvents.length; i++) {
      const event = targetEvents[i];
      if (
        currentManningVesselCrewId === event.enterprise_manning_vessel_crew_id
      ) {
        EventHelperCommon.changeEnterpriseManningVesselCrewId(
          event,
          previousEvent.enterprise_manning_vessel_crew_id
        );
      } else {
        _processReAssigin(targetEvents.slice(i + 1), event);
        break;
      }
    }
  }

  // イベント期間の繋がりがなく、乗船期間IDが同じ
  if (reason === UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_DATE) {
    // phantomGrupKeyを生成
    const phantomGrupKey = EventHelperCommon.genPhantomGroupKey();

    // 判定対象の乗船期間IDと同じイベントの乗船期間IDをnullに更新
    // サーバサイドに乗船期間IDを発行してもらうためにphantomGroupKeyを設定
    // 乗船期間IDが変わった時に再起呼び出し
    for (let i = 0; i < targetEvents.length; i++) {
      const event = targetEvents[i];
      if (
        currentManningVesselCrewId === event.enterprise_manning_vessel_crew_id
      ) {
        EventHelperCommon.changeEnterpriseManningVesselCrewId(event, null);
        EventHelperCommon.setPhantomGroupKey(event, phantomGrupKey);
      } else {
        _processReAssigin(targetEvents.slice(i + 1), event);
        break;
      }
    }
  }
};

/**
 * 乗船期間IDを更新する必要がある理由を定義する定数
 * @readonly
 * @enum {number}
 * @property {number} NO_UPDATE
 * - 更新不要
 * @property {number} PREVIOUS_STATUS_WAS_PLANNED
 * - 判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
 * @property {number} CURRENT_STATUS_WAS_PLANNED
 * - 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
 * @property {number} MISSMATCH_VESSEL_CREW_ID
 * - イベントが繋がっているが、乗船期間IDが異なる
 * @property {number} MISSMATCH_VESSEL_DATE
 * - イベント期間の繋がりがなく、乗船期間IDが同じ
 * @returns {UPDATE_VESSEL_CREW_ID_REASON}
 */
const UPDATE_VESSEL_CREW_ID_REASON = {
  NO_UPDATE: 0,
  PREVIOUS_STATUS_WAS_PLANNED: 1,
  CURRENT_STATUS_WAS_PLANNED: 2,
  MISSMATCH_VESSEL_CREW_ID: 3,
  MISSMATCH_VESSEL_DATE: 4,
};

/**
 * 乗船中様態のイベントの乗船期間IDを更新する必要があるかを判定するヘルパー関数
 * @param {CrewEventModel} previousEvent
 * @param {CrewEventModel} currentEvent
 * @returns {UPDATE_VESSEL_CREW_ID_REASON}
 */
const _shouldUpdateVesselCrewId = (previousEvent, currentEvent) => {
  //判定対象の前のイベントが元計画中イベントで、判定対象と繋がりがあるが乗船期間IDが異なる
  if (
    _wasPlanned(previousEvent) &&
    previousEvent.startDate === currentEvent.endDate &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.PREVIOUS_STATUS_WAS_PLANNED;
  }

  // 判定対象が元計画中イベントで、前のイベントと繋がりがあるが乗船期間IDが異なる
  if (
    _wasPlanned(currentEvent) &&
    previousEvent.endDate.getTime() === currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.CURRENT_STATUS_WAS_PLANNED;
  }

  // 上記以外の元計画中イベントは更新不要
  if (_wasPlanned(previousEvent) || _wasPlanned(currentEvent)) {
    return UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE;
  }

  // イベントが繋がっているが、乗船期間IDが異なる
  if (
    previousEvent.startDate.getTime() === currentEvent.endDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id !==
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_CREW_ID;
  }

  // イベント期間の繋がりがなく、乗船期間IDが同じ
  if (
    previousEvent.endDate.getTime() !== currentEvent.startDate.getTime() &&
    previousEvent.enterprise_manning_vessel_crew_id ===
      currentEvent.enterprise_manning_vessel_crew_id
  ) {
    return UPDATE_VESSEL_CREW_ID_REASON.MISSMATCH_VESSEL_DATE;
  }

  return UPDATE_VESSEL_CREW_ID_REASON.NO_UPDATE;
};

/**
 * イベント結合処理
 * @param {Store} store
 * @param {String} resourceId
 */
const _concateEventsAsync = async (store, resourceId) => {
  const targetsEvents = store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.resourceId === resourceId &&
      !EventHelperCommon.isSoftDeleted(event)
    );
  }, true);

  // 開始日でソート
  targetsEvents.sort(EventHelperCommon.sortByStartDate);

  // 下船中イベント結合
  // 下船中様態のイベントをフィルタリング
  const ashoreEvents = targetsEvents.filter(event => {
    return event.event_type === C.SCHEDULING_EVENT_TYPE.ASHORE;
  });
  if (ashoreEvents.length > 1) {
    const currentEvent = ashoreEvents[0];
    await _processConcatAhoreEventAsync(ashoreEvents.slice(1), currentEvent);
  }

  // 乗船中イベント結合
  // 乗船中のイベントをフィルタリング
  const vesselEvents = targetsEvents.filter(event => {
    return event.event_type === C.SCHEDULING_EVENT_TYPE.VESSEL;
  });
  if (vesselEvents.length > 1) {
    const currentEvent = vesselEvents[0];
    await _processConcatVesselEventAsync(vesselEvents.slice(1), currentEvent);
  }
};

/**
 * 乗船中イベント結合
 * @param {Array<EventModel>} vesselEvents CurrentEventの後続イベント
 * @param {EventModel} currentEvent
 * @returns
 */
const _processConcatVesselEventAsync = async (vesselEvents, currentEvent) => {
  if (vesselEvents.length === 0) {
    return;
  }
  const nextEvent = vesselEvents[0];

  // 乗船期間,役職ポジションが異なる場合は次のイベントを処理
  if (
    currentEvent.enterprise_manning_vessel_crew_id !==
      nextEvent.enterprise_manning_vessel_crew_id ||
    currentEvent.enterprise_manning_vessel_role_position_id !==
      nextEvent.enterprise_manning_vessel_role_position_id
  ) {
    // 乗船中の様態が異なる場合は次のイベントを処理
    await _processConcatVesselEventAsync(vesselEvents.slice(1), nextEvent);
    return;
  }

  // 乗船期間,役職ポジションが同じでイベントが繋がっている場合は結合処理
  if (currentEvent.endDate.getTime() === nextEvent.startDate.getTime()) {
    // 計画中 => 予定へ変更されたイベントの場合は、後続イベントの開始日を現在のイベントの終了日に更新
    if (_wasPlanned(currentEvent)) {
      await _concatEventAysc(nextEvent, currentEvent);
      await _processConcatVesselEventAsync(vesselEvents.slice(1), nextEvent);
    } else {
      await _concatEventAysc(currentEvent, nextEvent);
      await _processConcatVesselEventAsync(vesselEvents.slice(1), currentEvent);
    }
    // イベントが繋がっていない場合は次のイベントを処理
  } else {
    await _processConcatVesselEventAsync(vesselEvents.slice(1), nextEvent);
  }
};

/**
 * 下船中様態イベント結合処理
 * @param {Array<EventModel>} ashoreEvents CurrentEventの後続イベント
 * @param {EventModel} currentEvent
 * @returns
 */
const _processConcatAhoreEventAsync = async (ashoreEvents, currentEvent) => {
  if (ashoreEvents.length === 0) {
    return;
  }

  const nextEvent = ashoreEvents[0];
  if (
    currentEvent.endDate.getTime() === nextEvent.startDate.getTime() &&
    _isSameAshoreEvent(currentEvent, nextEvent)
  ) {
    // 同じ種類の下船中様態イベントで、終了日と開始日が同じ場合は結合
    await _concatEventAysc(currentEvent, nextEvent);
    await _processConcatAhoreEventAsync(ashoreEvents.slice(1), currentEvent);
  } else {
    // 結合できない場合は次のイベントを処理
    await _processConcatAhoreEventAsync(ashoreEvents.slice(1), nextEvent);
  }
};

/**
 * イベントを結合するヘルパー関数
 * event1の開始日がevent2の開始日よりも後の場合は、event1の開始日をevent2の開始日に更新し、
 * event1の終了日がevent2の終了日よりも前の場合は、event1の終了日をevent2の終了日に更新する。
 * 最後に、event2をソフトデリートする。
 * @param {*} event1
 * @param {*} event2
 */
const _concatEventAysc = async (event1, event2) => {
  if (event1.startDate.getTime() > event2.startDate.getTime()) {
    await event1.setStartDate(event2.startDate, false);
  }

  if (event1.endDate.getTime() < event2.endDate.getTime()) {
    await event1.setEndDate(event2.endDate, false);
  }

  EventHelperCommon.softDelete(event2);
};

/**
 * 計画中 => 予定へ変更されたイベントか
 * @param {*} event
 * @returns
 */
const _wasPlanned = event => {
  return (
    event.isFieldModified("scheduling_status") &&
    event.scheduling_status === C.SCHEDULING_STATUS.SCHEDULED
  );
};

/**
 * 重なっているイベントを調整する
 * @param {EventModel} editedEvent
 * @param {*} targetEvents
 */
const _ajastPushAsync = async (editedEvent, targetEvents) => {
  // 重なっているイベントを取得
  const overlappingEvents = targetEvents.filter(event => {
    return EventHelperCommon.isOverlapping(editedEvent, event);
  });

  for (const overlap of overlappingEvents) {
    // 包含されている場合は終了
    if (
      EventHelperCommon.isContained(editedEvent, overlap) ||
      EventHelperCommon.isContained(overlap, editedEvent)
    ) {
      continue;
    }

    // 左側が重なっている場合は終了日を更新
    if (EventHelperCommon.isOverlappingLeft(editedEvent, overlap)) {
      await overlap.setEndDate(editedEvent.startDate);
    }

    // 右側が重なっている場合は開始日を更新
    if (EventHelperCommon.isOverlappingRight(editedEvent, overlap)) {
      await overlap.setStartDate(editedEvent.endDate, false);
    }
  }
};

const _splitEventAsync = async (
  targetEvent,
  eventStore,
  startDate,
  endDate
) => {
  const copyEvent = await EventHelperCommon.copyEventAsync(
    targetEvent,
    eventStore
  );
  await copyEvent.setStartDate(endDate, false);
  await targetEvent.setEndDate(startDate);
};

// 同種類のイベントか判定
const _isSameEventType = (event1, event2) => {
  if (event1.event_type !== event2.event_type) {
    return false;
  }

  switch (event1.event_type) {
    case C.SCHEDULING_EVENT_TYPE.ASHORE:
      return _isSameAshoreEvent(event1, event2);
    case C.SCHEDULING_EVENT_TYPE.VESSEL:
      return _isSameVesselEvent(event1, event2);
    default:
      return false;
  }
};

/**
 *
 * @param {*} event1
 * @param {*} event2
 * @returns
 */
const _isSameVesselEvent = (event1, event2) => {
  return (
    event1.resourceId === event2.resourceId &&
    event1.enterprise_manning_vessel_id ===
      event2.enterprise_manning_vessel_id &&
    event1.enterprise_manning_vessel_crew_id ===
      event2.enterprise_manning_vessel_crew_id
  );
};

/**
 * 同じ種類の下船中様態イベントかを判定するヘルパー関数
 * @param {*} event1
 * @param {*} event2
 * @returns
 */
const _isSameAshoreEvent = (event1, event2) => {
  return (
    event1.resourceId === event2.resourceId &&
    event1.enterprise_manning_status_id ===
      event2.enterprise_manning_status_id &&
    event1.enterprise_manning_vessel_role_position_id ===
      event1.enterprise_manning_vessel_role_position_id
  );
};

/**
 *
 * @param {*} store イベントストア
 * @param {*} editedEvent 変更されたイベント
 * @param {*} isActiveOnly 削除フラグが立っていないイベントのみを対象とするか
 * @returns
 */
const _getTargetEvents = (store, editedEvent, isActiveOnly) => {
  return store.query(event => {
    return (
      EventHelperCommon.isScheduled(event) &&
      event.resourceId === editedEvent.resourceId &&
      event.id !== editedEvent.id &&
      (!isActiveOnly || !EventHelperCommon.isSoftDeleted(event))
    );
  }, true);
};
